import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { getPortalMenuItemWithPermissionsCountFx } from '@nx-frontend/shared/store/feature-menu-tree';
import { getUserByIdFx } from '@nx-frontend/shared/store/users-store';
import { getWorkspaceMemberByUserIdFx } from '@nx-frontend/shared/store/workspace-members';
import { ManageMembersInvite, OrgMemberRole, PopupType } from '@nx-frontend/shared/types';
import '../dots-animation.scss';
import {
  $context,
  $manageMembersWorkspaceMemberUserIds,
  $portalClientAccess,
  $portalConsoleAccess,
  $sortedByRoleMembers,
  getManageMembersDataFx,
  inviteToPortalFx,
} from '../store';
import {
  getPortalClientOrgRolesFromAccess,
  getPortalConsoleOrgRolesFromAccess,
  userHasWorkspaceAccess,
} from '../utils';
import OrgAccessItem, { AccessItems } from './org-access-item';

export function PortalList() {
  const [closeModal] = useEvent([closeModalEvent]);
  const [
    members,
    context,
    portalConsoleAccess,
    portalClientAccess,
    workspaceMemberUserIds,
    getUserById,
    inviteToPortal,
    getWorkspaceMemberByUserId,
    getManageMembersData,
    openModal,
    getPortalMenuItemWithPermissionsCount,
  ] = useUnit([
    $sortedByRoleMembers,
    $context,
    $portalConsoleAccess,
    $portalClientAccess,
    $manageMembersWorkspaceMemberUserIds,
    getUserByIdFx,
    inviteToPortalFx,
    getWorkspaceMemberByUserIdFx,
    getManageMembersDataFx,
    openModalEvent,
    getPortalMenuItemWithPermissionsCountFx,
  ]);

  const t = useTranslations('features');
  const [draft, setDraft] = useState<{
    [key: number]: AccessItems;
  }>({});
  const [isSending, setIsSending] = useState(false);
  const [isOnlySharedAvailable, setIsOnlySharedAvailable] = useState(false);

  const availablePortalOrgRoles = getPortalClientOrgRolesFromAccess(portalClientAccess);
  const availableConsoleOrgAccessRoles = getPortalConsoleOrgRolesFromAccess(portalConsoleAccess);

  useEffect(() => {
    (async () => {
      const count = await getPortalMenuItemWithPermissionsCount();
      if (count) {
        setIsOnlySharedAvailable(true);
      }
    })();
  }, [getPortalMenuItemWithPermissionsCount]);

  const preparedItems = useMemo(() => {
    return members.reduce((acc, member) => {
      if (member.role === OrgMemberRole.GUEST && !workspaceMemberUserIds.includes(member.userId)) {
        return acc;
      }
      const isOwner = member.role === OrgMemberRole.OWNER;
      const hasAccessInitial = userHasWorkspaceAccess(member.userId, workspaceMemberUserIds);
      const roleHasConsoleAccess = availableConsoleOrgAccessRoles.includes(member.role);
      const hasConsoleAccess = roleHasConsoleAccess && hasAccessInitial;
      const hasAccessByRole = availablePortalOrgRoles.includes(member.role);
      const initialAccessValue = hasAccessInitial ? AccessItems.ACCESS_GRANTED : AccessItems.NO_ACCESS;
      acc.push({
        userId: member.userId,
        initialAccessValue,
        accessValue: draft[member.userId] ?? initialAccessValue,
        changeDisabled: isOwner,
        role: member.role,
        highlightedRole: hasConsoleAccess,
        roleHasConsoleAccess,
        roleRestricted: !hasAccessByRole,
      });
      return acc;
    }, [] as { userId: number; initialAccessValue: AccessItems; accessValue: AccessItems; changeDisabled: boolean; role: OrgMemberRole; roleRestricted: boolean; highlightedRole: boolean; roleHasConsoleAccess: boolean }[]);
  }, [availableConsoleOrgAccessRoles, availablePortalOrgRoles, draft, members, workspaceMemberUserIds]);
  const handleChange = (
    { userId, initialAccessValue, role }: { userId: number; initialAccessValue: AccessItems; role: OrgMemberRole },
    value: AccessItems
  ) => {
    if (initialAccessValue !== value && value === AccessItems.NO_ACCESS && role !== OrgMemberRole.CLIENT) {
      openModal({
        title: t('manage-members.delete_member_from_portal'),
        content: PopupType.DIALOG,
        showCloseButton: false,
        payload: {
          payloadType: PopupType.DIALOG,
          text: (
            <div>
              {t('manage-members.member_will_lose_access')}
              <br />
              {t('manage-members.you_will_be_able_to_restore')}
            </div>
          ),
          buttons: [
            {
              text: t('manage-members.ok'),
              main: true,
              size: 'xl',
              onClick: () => {
                closeModal();
              },
            },
          ],
        },
      });
    }
    const newDraft = { ...draft, ...{ [userId]: value } };
    setDraft(newDraft);
  };

  const handleSubmit = async () => {
    const filteredDraft = preparedItems.reduce((acc, item) => {
      const userId = item.userId;
      const draftItem = acc[userId];
      if (draftItem && draftItem === item.initialAccessValue) {
        delete acc[userId];
      }
      return acc;
    }, draft);

    if (!Object.keys(filteredDraft).length) {
      return;
    }

    const invites: ManageMembersInvite[] = [];
    const deleteIds: string[] = [];

    for (const id of Object.keys(filteredDraft)) {
      const value = filteredDraft[Number(id)];
      const user = await getUserById({ id: Number(id) });
      if (value === AccessItems.NO_ACCESS) {
        const workspaceMember = await getWorkspaceMemberByUserId({ id: Number(id) });
        if (workspaceMember) {
          deleteIds.push(workspaceMember.globalId);
        }
      } else {
        invites.push({
          email: user.email,
          role: 'editor',
          isFullAccess: value === AccessItems.FULL_ACCESS,
        });
      }
    }

    setIsSending(true);
    await inviteToPortal({ invites, deleteIds, workspaceId: context.workspaceId, portalUrl: context.portalUrl });
    await getManageMembersData();
    setIsSending(false);
    setDraft({});
  };

  return (
    <>
      <div className="w-full h-full min-h-0 overflow-y-auto mt-3">
        {preparedItems.map((item, index) => (
          <OrgAccessItem
            key={item.userId}
            userId={item.userId}
            initialAccessValue={item.initialAccessValue}
            accessValue={item.accessValue}
            onChange={(value) => handleChange(item, value)}
            isLast={members.length - 1 === index}
            role={item.role}
            roleRestricted={item.roleRestricted}
            changeDisabled={item.changeDisabled}
            highlightedRole={item.highlightedRole}
            portalId={context.portalId}
            isPortalItem={true}
            isOnlySharedAvailable={isOnlySharedAvailable && !item.roleHasConsoleAccess}
          />
        ))}
      </div>
      <div className="flex mt-3 justify-end gap-4">
        <Button type={ButtonType.STROKED} size={ButtonSize.XL} onClick={() => closeModal()}>
          {t('manage-members.cancel')}
        </Button>
        <Button
          type={ButtonType.FLAT}
          size={ButtonSize.XL}
          disabled={isSending}
          className="min-w-[172px]"
          onClick={handleSubmit}
        >
          {!isSending && <span>{t('manage-members.save_changes')}</span>}
          {isSending && (
            <span className="flex">
              {t('manage-members.saving_changes')}{' '}
              <div className="flex ml-1">
                <span className="dot">.</span>
                <span className="dot">.</span>
                <span className="dot">.</span>
              </div>{' '}
            </span>
          )}
        </Button>
      </div>
    </>
  );
}

export default PortalList;
